import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES } from '../../constants';

// Loading spinner component
const LoadingSpinner = () => (
  <div className='min-h-screen flex items-center justify-center bg-gray-50'>
    <div className='flex flex-col items-center'>
      <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600'></div>
      <p className='mt-4 text-gray-600'>Loading...</p>
    </div>
  </div>
);

const ProtectedRoute = ({
  children,
  requiredRole = null,
  requiredPermission = null,
}) => {
  const location = useLocation();
  const { isAuthenticated, isLoading, hasRole, hasPermission, user } =
    useAuth();

  // Show loading spinner while auth is initializing
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // Check role requirement if specified
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50'>
        <div className='text-center'>
          <h1 className='text-2xl font-bold text-gray-900 mb-2'>
            Access Denied
          </h1>
          <p className='text-gray-600'>
            You don't have the required permissions to access this page.
          </p>
          <p className='text-sm text-gray-500 mt-2'>
            Required role: {requiredRole}. Your role: {user?.role}
          </p>
        </div>
      </div>
    );
  }

  // Check permission requirement if specified
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50'>
        <div className='text-center'>
          <h1 className='text-2xl font-bold text-gray-900 mb-2'>
            Access Denied
          </h1>
          <p className='text-gray-600'>
            You don't have the required permissions to access this page.
          </p>
          <p className='text-sm text-gray-500 mt-2'>
            Required permission: {requiredPermission}
          </p>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
